import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, ArrowLeft, RefreshCw, Download, AlertTriangle, TrendingUp, TrendingDown,
  Users, FileText, Calendar, DollarSign, Loader2, ChevronRight, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";

const BUCKET_COLORS = {
  "current": "hsl(158, 64%, 40%)",    // Green - healthy
  "1-30": "hsl(38, 92%, 50%)",        // Yellow - warning
  "31-60": "hsl(25, 95%, 53%)",       // Orange - concern
  "61-90": "hsl(4, 74%, 55%)",        // Red - serious
  "90+": "hsl(0, 72%, 48%)"           // Dark red - critical
};

const BUCKET_BG_COLORS = {
  "current": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  "1-30": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  "31-60": "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  "61-90": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "90+": "bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300"
};

export default function PaymentAgingPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [agingData, setAgingData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [agingRes, historyRes] = await Promise.all([
        api.get("/reports/payment-aging"),
        api.get("/reports/payment-aging/history?months=6")
      ]);
      setAgingData(agingRes.data);
      setHistoryData(historyRes.data);
    } catch (error) {
      console.error("Error fetching aging data:", error);
      toast.error("Failed to load payment aging report");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchClientDetail = async (clientId) => {
    setIsLoadingClient(true);
    try {
      const response = await api.get(`/reports/payment-aging/by-client/${clientId}`);
      setClientDetail(response.data);
    } catch (error) {
      console.error("Error fetching client detail:", error);
      toast.error("Failed to load client details");
    } finally {
      setIsLoadingClient(false);
    }
  };

  const handleExport = async (format) => {
    try {
      if (format === "csv") {
        const response = await api.get("/reports/payment-aging/export?format=csv", {
          responseType: "blob"
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `payment_aging_${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Report exported successfully");
      } else {
        const response = await api.get("/reports/payment-aging/export?format=json");
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `payment_aging_${new Date().toISOString().split("T")[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Report exported successfully");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background" data-testid="payment-aging-page">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  const bucketChartData = agingData?.aging_summary?.map(bucket => ({
    name: bucket.label,
    amount: bucket.total_amount,
    count: bucket.count,
    fill: BUCKET_COLORS[bucket.bucket]
  })) || [];

  const pieChartData = agingData?.aging_summary?.filter(b => b.total_amount > 0).map(bucket => ({
    name: bucket.label,
    value: bucket.total_amount,
    fill: BUCKET_COLORS[bucket.bucket]
  })) || [];

  const historyChartData = historyData?.data_points?.map(point => ({
    date: point.date.substring(5), // MM-DD format
    "Total AR": point.total_receivables,
    "Overdue": point.total_overdue,
    "Current": point.bucket_current
  })) || [];

  const getTrendIcon = () => {
    if (!historyData) return null;
    if (historyData.trend === "improving") {
      return <TrendingDown className="h-4 w-4 text-green-500" />;
    } else if (historyData.trend === "worsening") {
      return <TrendingUp className="h-4 w-4 text-red-500" />;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="payment-aging-page">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Clock className="h-6 w-6 text-primary" />
                  Payment Aging Report
                </h1>
                <p className="text-sm text-muted-foreground">
                  Track overdue payments with 30/60/90 day aging buckets
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                data-testid="refresh-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Select onValueChange={handleExport}>
                <SelectTrigger className="w-[140px]" data-testid="export-select">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">Export CSV</SelectItem>
                  <SelectItem value="json">Export JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-receivables-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Receivables
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(agingData?.total_receivables)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {agingData?.by_client?.length || 0} clients with outstanding invoices
              </p>
            </CardContent>
          </Card>

          <Card data-testid="total-overdue-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Total Overdue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(agingData?.total_overdue)}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Progress 
                  value={agingData?.overdue_percentage || 0} 
                  className="h-2 flex-1"
                />
                <span className="text-xs text-muted-foreground">
                  {agingData?.overdue_percentage?.toFixed(1)}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="avg-days-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Avg Days Outstanding
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {agingData?.average_days_outstanding?.toFixed(0) || 0} days
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                {getTrendIcon()}
                <span>
                  {historyData?.trend === "improving" ? "Improving" : 
                   historyData?.trend === "worsening" ? "Worsening" : "Stable"}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="at-risk-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                At Risk (60+ Days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const atRisk = agingData?.aging_summary?.filter(b => 
                  ["61-90", "90+"].includes(b.bucket)
                ).reduce((sum, b) => sum + b.total_amount, 0) || 0;
                const atRiskPct = agingData?.total_receivables > 0 
                  ? (atRisk / agingData.total_receivables * 100) 
                  : 0;
                return (
                  <>
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {formatCurrency(atRisk)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {atRiskPct.toFixed(1)}% of total receivables
                    </p>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList data-testid="aging-tabs">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="by-client">By Client</TabsTrigger>
            <TabsTrigger value="invoices">Overdue Invoices</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Aging Buckets Bar Chart */}
              <Card data-testid="aging-buckets-chart">
                <CardHeader>
                  <CardTitle>Aging Buckets</CardTitle>
                  <CardDescription>Outstanding amounts by aging period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bucketChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis dataKey="name" type="category" width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                          {bucketChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Pie Chart Distribution */}
              <Card data-testid="aging-distribution-chart">
                <CardHeader>
                  <CardTitle>Distribution</CardTitle>
                  <CardDescription>Percentage breakdown by aging bucket</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aging Summary Table */}
            <Card data-testid="aging-summary-table">
              <CardHeader>
                <CardTitle>Aging Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aging Bucket</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agingData?.aging_summary?.map((bucket) => (
                      <TableRow key={bucket.bucket}>
                        <TableCell>
                          <Badge className={BUCKET_BG_COLORS[bucket.bucket]}>
                            {bucket.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{bucket.count}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(bucket.total_amount)}
                        </TableCell>
                        <TableCell className="text-right">{bucket.percentage}%</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold bg-muted/50">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">
                        {agingData?.aging_summary?.reduce((sum, b) => sum + b.count, 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(agingData?.total_receivables)}
                      </TableCell>
                      <TableCell className="text-right">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Client Tab */}
          <TabsContent value="by-client" className="space-y-4">
            <Card data-testid="client-aging-table">
              <CardHeader>
                <CardTitle>Client Aging Breakdown</CardTitle>
                <CardDescription>Outstanding amounts by client and aging bucket</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">1-30</TableHead>
                      <TableHead className="text-right">31-60</TableHead>
                      <TableHead className="text-right">61-90</TableHead>
                      <TableHead className="text-right">90+</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agingData?.by_client?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No outstanding invoices found
                        </TableCell>
                      </TableRow>
                    ) : (
                      agingData?.by_client?.map((client) => (
                        <TableRow key={client.client_id} className="cursor-pointer hover:bg-muted/50">
                          <TableCell>
                            <div>
                              <div className="font-medium">{client.client_name}</div>
                              {client.client_email && (
                                <div className="text-xs text-muted-foreground">{client.client_email}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {client.current > 0 ? formatCurrency(client.current) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {client.days_1_30 > 0 ? formatCurrency(client.days_1_30) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {client.days_31_60 > 0 ? formatCurrency(client.days_31_60) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {client.days_61_90 > 0 ? formatCurrency(client.days_61_90) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {client.days_90_plus > 0 ? (
                              <span className="text-red-600 font-medium">
                                {formatCurrency(client.days_90_plus)}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(client.total_outstanding)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedClient(client);
                                fetchClientDetail(client.client_id);
                              }}
                              data-testid={`view-client-${client.client_id}`}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Overdue Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <Card data-testid="overdue-invoices-table">
              <CardHeader>
                <CardTitle>Overdue Invoices</CardTitle>
                <CardDescription>All invoices past their due date</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Days Overdue</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agingData?.overdue_invoices?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          <div className="flex flex-col items-center gap-2">
                            <FileText className="h-8 w-8 text-muted-foreground/50" />
                            <span>No overdue invoices - great job!</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      agingData?.overdue_invoices?.map((invoice) => (
                        <TableRow key={invoice.invoice_id}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{invoice.client_name}</TableCell>
                          <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                          <TableCell>{formatDate(invoice.due_date)}</TableCell>
                          <TableCell className="text-right">
                            <Badge className={BUCKET_BG_COLORS[invoice.bucket]}>
                              {invoice.days_overdue} days
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(invoice.amount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/invoices`)}
                                data-testid={`view-invoice-${invoice.invoice_id}`}
                              >
                                View
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card data-testid="aging-history-chart">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Aging Trend
                  {getTrendIcon()}
                  <Badge variant={
                    historyData?.trend === "improving" ? "default" :
                    historyData?.trend === "worsening" ? "destructive" : "secondary"
                  }>
                    {historyData?.trend || "stable"}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Historical receivables and overdue amounts over the past 6 months
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="Total AR"
                        stackId="1"
                        stroke="hsl(195, 72%, 36%)"
                        fill="hsl(195, 72%, 36%)"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="Overdue"
                        stackId="2"
                        stroke="hsl(4, 74%, 55%)"
                        fill="hsl(4, 74%, 55%)"
                        fillOpacity={0.5}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Summary Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{historyData?.data_points?.length || 0}</div>
                    <div className="text-sm text-muted-foreground">Data Points</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{historyData?.average_overdue_percentage?.toFixed(1)}%</div>
                    <div className="text-sm text-muted-foreground">Avg Overdue %</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold capitalize">{historyData?.trend}</div>
                    <div className="text-sm text-muted-foreground">Trend Direction</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">6</div>
                    <div className="text-sm text-muted-foreground">Months History</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedClient?.client_name} - Aging Detail</DialogTitle>
            <DialogDescription>
              {selectedClient?.client_email}
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingClient ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : clientDetail ? (
            <div className="space-y-4">
              {/* Aging Breakdown */}
              <div className="grid grid-cols-5 gap-2">
                {[
                  { label: "Current", value: clientDetail.aging_breakdown?.current, bucket: "current" },
                  { label: "1-30", value: clientDetail.aging_breakdown?.days_1_30, bucket: "1-30" },
                  { label: "31-60", value: clientDetail.aging_breakdown?.days_31_60, bucket: "31-60" },
                  { label: "61-90", value: clientDetail.aging_breakdown?.days_61_90, bucket: "61-90" },
                  { label: "90+", value: clientDetail.aging_breakdown?.days_90_plus, bucket: "90+" }
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`text-center p-2 rounded-lg ${
                      item.value > 0 ? BUCKET_BG_COLORS[item.bucket] : "bg-muted"
                    }`}
                  >
                    <div className="text-xs font-medium">{item.label}</div>
                    <div className="text-sm font-bold">
                      {item.value > 0 ? formatCurrency(item.value) : "-"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-2xl font-bold">
                  {formatCurrency(clientDetail.total_outstanding)}
                </div>
                <div className="text-sm text-muted-foreground">Total Outstanding</div>
              </div>

              {/* Invoices List */}
              <div>
                <h4 className="font-medium mb-2">Outstanding Invoices ({clientDetail.invoice_count})</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {clientDetail.invoices?.map((inv) => (
                    <div
                      key={inv.invoice_id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{inv.invoice_number}</div>
                        <div className="text-xs text-muted-foreground">
                          Due: {formatDate(inv.due_date)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{formatCurrency(inv.amount)}</div>
                        <Badge className={BUCKET_BG_COLORS[inv.bucket]} variant="secondary">
                          {inv.days_overdue > 0 ? `${inv.days_overdue} days overdue` : "Current"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSelectedClient(null)}>
                  Close
                </Button>
                <Button onClick={() => navigate("/invoices")}>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Reminder
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
